import { useTheme } from '@mui/styles';
import { ProductTypes } from '../../../../../../types';
import type { UiSettings } from '../../../../quotes/prime-x/types';
import { isExcessUwProductType } from '../../../../quotes/prime-x/utils';
import type { MidtermEndorsementTemplate, StepPrg } from '../api/types';
import { ENDORSEMENT_STEPS } from '../constants';
import {
  StepAttentionReason,
  StepStatus,
  type ChipVariant,
  type EndorsementState,
  type UI_Step,
} from '../types';
import {
  DiffType,
  type Diff,
  type ESPolicyPx,
} from '../../../../../../api/policies.api.types';
import type { QuoteFieldValue } from '../../../../../../api/quotes.api.types';
import type { Nullable } from '../../../../../../types/common/utility';
import type { TowerValue } from '../../../../../../components/tower';
import type { RaterXPreset } from '../../../../quotes/prime-x/api/types/rater-x-preset';
import { isRaterDeclined } from '../../../../quotes/prime-x/views/excess-request-quote/utils/isRaterDeclined';
import { buildRaterItemFieldIds } from '../../../../quotes/prime-x/transformers/coverages';

/**
 * Reads the single endorsable tower value — the Cowbell attachment (aggregate)
 * limit — out of an excess policy's `globalValue`. Mirrors how the coverages
 * endorsement step (EndorsementExcessTower) resolves it. Returns null when the
 * policy is not excess or the tower has no attachment layer.
 */
export function readCowbellAttachmentLimit(
  globalValue: Nullable<TowerValue>
): Nullable<number> {
  return globalValue?.props?.cowbellAttachment?.props?.limit?.value ?? null;
}

/**
 * Whether the Coverages section should render in the endorsement summary /
 * revision timeline. Shows on a change to any rater item (coverage or rate
 * modifier); form answers share `dynamicFields` but are not rater items. For
 * excess, the only endorsable tower value (the Cowbell attachment limit) lives
 * in `globalValue`, so a limit-only change must also surface the section —
 * otherwise it stays hidden while Issue remains enabled. Primary is unchanged:
 * its first-party global diffs at the top level, not under `globalValue`.
 */
export function shouldShowCoveragesSummary(
  diff: Nullable<Diff<ESPolicyPx>>,
  isExcess: boolean,
  raterItemFieldIds: ReadonlySet<string>
): boolean {
  return (
    changedDynamicFieldIds(diff).some((id) => raterItemFieldIds.has(id)) ||
    (isExcess && !!diff?.diffs?.globalValue)
  );
}

export function raterItemFieldIds(
  previous: Nullable<ESPolicyPx['policy']['raterResponse']>,
  next: Nullable<ESPolicyPx['policy']['raterResponse']>
): ReadonlySet<string> {
  return new Set(
    Array.from(buildRaterItemFieldIds(previous)).concat(
      Array.from(buildRaterItemFieldIds(next))
    )
  );
}

export function changedFormAnswerIds(
  diff: Nullable<Diff<ESPolicyPx>>,
  raterItemFieldIds: ReadonlySet<string>
): string[] {
  return changedDynamicFieldIds(diff).filter(
    (id) => !raterItemFieldIds.has(id)
  );
}

function changedDynamicFieldIds(diff: Nullable<Diff<ESPolicyPx>>): string[] {
  return Object.keys(diff?.diffs?.dynamicFields?.diffs ?? {});
}

/**
 * The endorsement options a user may pick on the settings screen. Only the
 * excess (PRIME_ONE_PLUS_US) Coverages option is gated today — it rolls out
 * behind `endorsements_x_coverages_PRIME_ONE_PLUS_US`. Every other product
 * keeps its Coverages option regardless of the flag, so the gate can never
 * dark-launch Prime One US / P100 Pro / Prime Tech. See FPX-7613.
 *
 * Deliberately scoped to what the picker offers rather than filtering the
 * fetched templates: `withStepTemplate` resolves a step's template out of that
 * same list and error-screens when it is missing, so a draft that already holds
 * a gated step stays coherent instead of breaking when the flag goes off.
 */
export function selectEndorsableTemplates(
  templates: MidtermEndorsementTemplate[],
  productType: string | undefined,
  isExcessCoveragesEnabled: boolean
): MidtermEndorsementTemplate[] {
  const isCoveragesGated =
    isExcessUwProductType(productType) && !isExcessCoveragesEnabled;

  if (!isCoveragesGated) {
    return templates;
  }

  return templates.filter(
    (template) => template.key !== ENDORSEMENT_STEPS.COVERAGES
  );
}

const NEEDS_NO_ATTENTION = {
  attentionReason: null,
  attentionTriggeredByStepId: null,
};

export function deriveInitialStepperState(data: EndorsementState) {
  const prgStepMap = new Map<string, StepPrg>(
    data.progress.steps.map((s) => [s.templateId, s])
  );

  const initialSteps: string[] = [ENDORSEMENT_STEPS.MTE_SETTINGS];

  const ui_steps: UI_Step[] = [
    {
      title: ENDORSEMENT_STEPS.MTE_SETTINGS,
      isCompleted: !!data.progress.steps.length,
      key: ENDORSEMENT_STEPS.MTE_SETTINGS,
      id: ENDORSEMENT_STEPS.MTE_SETTINGS,
      ...NEEDS_NO_ATTENTION,
    },
  ];

  for (const template of data.templates.templates) {
    const prgStep = prgStepMap.get(template.id);

    const isCompleted = prgStep?.status === StepStatus.COMPLETED;

    if (prgStep) {
      ui_steps.push({
        title: template.title,
        isCompleted,
        key: prgStep?.templateKey,
        id: prgStep.id,
        attentionReason: prgStep.attentionReason ?? null,
        attentionTriggeredByStepId: prgStep.attentionTriggeredByStepId ?? null,
      });

      initialSteps.push(prgStep?.templateKey);
    }
  }

  initialSteps.push(ENDORSEMENT_STEPS.MTE_SUMMARY);
  ui_steps.push({
    title: ENDORSEMENT_STEPS.MTE_SUMMARY,
    isCompleted: false,
    key: ENDORSEMENT_STEPS.MTE_SUMMARY,
    id: ENDORSEMENT_STEPS.MTE_SUMMARY,
    ...NEEDS_NO_ATTENTION,
  });

  const startStep =
    ui_steps.find((s) => !s.isCompleted)?.key ?? ENDORSEMENT_STEPS.MTE_SETTINGS;

  return {
    initialSteps,
    startStep,
    ui_steps,
  };
}

export function findTriggeringStepTitle(
  triggeredByStepId: Nullable<string>,
  steps: UI_Step[]
): Nullable<string> {
  return steps.find((step) => step.id === triggeredByStepId)?.title ?? null;
}

export function shouldOfferErrorRepair(
  attentionReason: Nullable<StepAttentionReason>,
  raterX: Nullable<RaterXPreset>
): boolean {
  return (
    attentionReason === StepAttentionReason.RATER_REJECTED_CHANGE &&
    raterX?.valid === false &&
    !isRaterDeclined(raterX)
  );
}

const ZSP_COUNTY_RATED_STATES = ['KY', 'NY'];

// TODO(eric, ZBEL-3374): remove once ZSP coverage-change MTEs can re-rate a county change.
// fir-account ZspMteCityZipLockGuard enforces the same rule server-side.
export function cityZipLockReason(
  productType: Nullable<string>,
  state: Nullable<string>
): string | undefined {
  const upperState = state?.toUpperCase() ?? '';
  if (
    productType !== ProductTypes.zsp ||
    !ZSP_COUNTY_RATED_STATES.includes(upperState)
  ) {
    return undefined;
  }
  return `Changing city or zip code is not supported for ZSP policies in ${upperState} at this time`;
}

export function deriveUiSettings(data: EndorsementState) {
  const productType = data.policy?.policy.product;
  const isAdmittedPrimeXProduct = productType == ProductTypes.p100_pro;
  const isPrimeOneTech = productType == ProductTypes.prime_one_tech;

  return {
    showCoverageTerritorialLimit: isPrimeOneTech,
    showCoverageBasisOfCover: isPrimeOneTech,
    showCoverageWaitingPeriods: !isAdmittedPrimeXProduct,
    showCoverageRetroactiveDate: !isAdmittedPrimeXProduct,
    showGlobalWaitingPeriod: !isAdmittedPrimeXProduct,
    showCoverageEndDate: !isAdmittedPrimeXProduct,
    showClassDescription: productType === ProductTypes.prime_tech,
  } as UiSettings;
}

export const useColorScheme = (variant: ChipVariant) => {
  const theme = useTheme();

  if (variant === 'New') {
    return {
      [DiffType.ADDED]: theme.config.colors.cowbellAccent,

      [DiffType.CHANGED]: theme.config.colors.snackYellow,
    };
  }

  return {
    [DiffType.REMOVED]: theme.config.colors.paleTomato,
  };
};

export function transformRecordDfToList(
  df: Nullable<Record<string, QuoteFieldValue>>
) {
  return Object.entries(df || {}).map(([key, entry]) => {
    return {
      ...entry,
      fieldId: key,
      value: entry,
    };
  });
}

export function checkForDiffInFields(
  diff: Nullable<Diff<any>>,
  fields: string[]
) {
  if (!diff?.diffs) {
    return false;
  }
  return fields.some((field) => !!diff.diffs?.[field]?.diffType);
}
